import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateEvent,
  updateItems,
  updateConfig,
} from '../../redux/slices/eventSlice';
import BaseInfo from './BaseInfo';
import EditInfo from './EditInfo';
import Items from './Items';
import Organizations from './Organizations';
import Finance from './Finance';
import Statement from './Statement';
import Players from './Players';
import ItemSchedules from './ItemSchedules';
import Scores from './Scores';
import Referees from './Referees';
import PlayerSchedule from './PlayerSchedule';
import ItemsContent from './ItemsContent';
import PlayerExit from './PlayerExit';
import PageTransition from '../../components/PageTransition';

import request from '@/request';

const MenuButton = ({ menu, index, selectedMenu, onMenuChange }) => {
  const style = (info) => {
    if (!info.props.arrow) {
      return {
        container: {
          backgroundColor: 'rgba(53, 71, 125, 0.8)',
          color: '#fff',
          marginLeft: -90,
          borderRadius: 8,
        },
      };
    }
    return {};
  };

  const [expanded, setExpanded] = useState(() =>
    Boolean(menu.children && menu.children.some((c) => c.key === selectedMenu))
  );

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded((s) => !s);
  };

  const menuItem = (
    <div
      onClick={() => {
        if (menu.disabled) return;
        if (menu.children) {
          handleToggle({ stopPropagation: () => {} });
        } else {
          onMenuChange(index);
        }
      }}
      role="button"
      tabIndex={menu.disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!menu.disabled && (e.key === 'Enter' || e.key === ' ')) {
          if (menu.children) {
            handleToggle({ stopPropagation: () => {} });
          } else {
            onMenuChange(index);
          }
        }
      }}
      className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-between gap-2 ${
        menu.disabled
          ? 'bg-slate-50 text-slate-400 opacity-60 cursor-not-allowed'
          : index === selectedMenu && !menu.children
            ? 'bg-blue-100 text-blue-700 shadow-sm cursor-pointer'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 cursor-pointer'
      }`}
    >
      <div className="flex items-center gap-2">
        {menu.icon}
        <span>{menu.name}</span>
      </div>
      {menu.children ? (
        <button
          onClick={handleToggle}
          type="button"
          className="text-slate-400 hover:text-slate-600 px-2"
          aria-label="toggle submenu"
        >
          <svg
            className={`w-4 h-4 transform transition-transform ${expanded ? 'rotate-90' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 6l6 6-6 6"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );

  const submenu = menu.children ? (
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="ml-6 mt-1 space-y-1">
        {menu.children.map((child, ci) => (
          <button
            key={ci}
            onClick={() => !menu.disabled && onMenuChange(child.key)}
            disabled={menu.disabled}
            className={`w-full text-left px-4 py-2 text-sm rounded-lg transition-all duration-150 ${
              child.key === selectedMenu
                ? 'bg-blue-50 text-blue-700 font-medium'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            {child.name}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const content = (
    <>
      {menuItem}
      {submenu}
    </>
  );

  return menu.tip && menu.disabled ? (
    <Tooltip title={menu.tip} placement="right" styles={style} arrow={false}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default function Index() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { event } = useSelector((state) => state.event);
  const [searchParams, setSearchParams] = useSearchParams();

  // 从 URL 读取菜单索引，默认为 0
  const initialMenu = parseInt(searchParams.get('tab') || '0', 10);
  const [selectedMenu, setSelectedMenu] = useState(initialMenu);

  // 菜单改变时更新 URL 参数
  const handleMenuChange = useCallback(
    (index) => {
      setSelectedMenu(index);
      setSearchParams({ tab: index.toString() }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    (async () => {
      const itemTypes = await request.get('/events/itemType/all');
      const groupTypes = await request.get('/events/itemGroup/all');
      dispatch(updateConfig({ itemTypes, groupTypes }));
    })();
  }, [dispatch]);

  const menus = useMemo(
    () => [
      {
        name: '赛事信息',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
        ),
      },
      {
        name: '赛项管理',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z"
            />
          </svg>
        ),
        tip: '赛事信息填写后可编辑',
        disabled: !event?.qualification?.length,
        children: [
          { name: '赛项创建', key: 1 },
          { name: '计分设置', key: 9 },
        ],
      },
      {
        name: '参赛单位报名通道',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
      },
      {
        name: '选手管理',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
        children: [
          { name: '报名管理', key: 3 },
          { name: '选手退赛', key: 10 },
        ],
      },
      {
        name: '赛事裁判',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
      },
      {
        name: '赛程管理',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
      },
      {
        name: '赛程查询',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
      },
      {
        name: '成绩管理',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
      },
      {
        name: '财务管理',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
            />
          </svg>
        ),
        tip: '赛事发布后可编辑',
        disabled: !event?.id || event.reviewStatus !== 2,
        children: [
          { name: '订单记录', key: 8 },
          { name: '对账单', key: 11 },
        ],
      },
    ],
    [event]
  );

  const loadItems = useCallback(async () => {
    if (params.id) {
      const data = await request.get(`/events/item/list/${params.id}`);
      dispatch(updateItems(data));
    }
  }, [dispatch, params.id]);

  useEffect(() => {
    if (params.id) {
      request.get(`/events/info/${params.id}`).then((data) => {
        dispatch(updateEvent(data));
      });
    }
  }, [dispatch, params.id]);

  const onSave = useCallback(
    async (id) => {
      if (event.id === id) {
        const data = await request.get(`/events/info/${id}`);
        dispatch(updateEvent(data));
      } else {
        navigate(`/event/${id}`, { replace: true });
      }
    },
    [dispatch, event.id, navigate]
  );

  const canEdit = useMemo(() => {
    return event.status <= 0 || !event.id;
  }, [event]);

  const baseProps = { event, items: event.items || [] };

  return (
    <PageTransition>
      {/* Left Sidebar - Menu */}
      <div className="w-42 flex-shrink-0 mr-6">
        <div className="fixed top-20 space-y-1 w-42">
          <button
            onClick={() => navigate('/', { replace: true })}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-200 cursor-pointer mr-4"
            title="返回主页"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>返回</span>
          </button>
          {menus.map((menu, index) => (
            <MenuButton
              key={index}
              menu={menu}
              index={index}
              selectedMenu={selectedMenu}
              onMenuChange={handleMenuChange}
            />
          ))}
        </div>
      </div>

      {/* Right Content Area */}
      <div className="space-y-6 w-[calc(100vw-15rem)]">
        {/* Rejection Notice */}
        {event.status === -1 && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                {/* Warning Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center ring-4 ring-red-50">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-red-700 leading-relaxed text-left">
                      您的赛事申请未通过审核，请根据以下原因进行修改后重新提交
                    </p>
                    <div className="text-sm text-slate-400 font-medium">
                      {event.updateAt || '-'}
                    </div>
                  </div>

                  <div className="text-sm text-slate-800 bg-white/70 rounded-lg px-4 py-3 border border-red-200 leading-relaxed text-left break-all">
                    {event.remark || '暂无说明'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="p-6 animate-slide-in-up bg-white rounded-xl shadow-sm"
          key={selectedMenu}
        >
          {selectedMenu === 0 && (
            <EditInfo event={event} onComplete={onSave} editable={canEdit} />
          )}
          {selectedMenu === 1 && (
            <Items {...baseProps} refreshItems={loadItems} />
          )}
          {selectedMenu === 2 && <Organizations {...baseProps} />}
          {selectedMenu === 3 && <Players {...baseProps} />}
          {selectedMenu === 4 && <Referees {...baseProps} />}
          {selectedMenu === 5 && <ItemSchedules {...baseProps} />}
          {selectedMenu === 6 && <PlayerSchedule {...baseProps} />}
          {selectedMenu === 7 && <Scores {...baseProps} />}
          {selectedMenu === 8 && <Finance {...baseProps} />}
          {selectedMenu === 9 && <ItemsContent {...baseProps} />}
          {selectedMenu === 10 && <PlayerExit {...baseProps} />}
          {selectedMenu === 11 && <Statement {...baseProps} />}
        </div>
      </div>
    </PageTransition>
  );
}
